import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Link, SxProps, Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { COLORS } from "../../assets/theme";
import logo from "../../assets/softwarezz-icon.png";

/* ------------------------------------------------------------------ */
/* Navigation between auth pages: fade the current page out, then go.  */
/* ------------------------------------------------------------------ */

const LEAVE_MS = 220;

const AuthNavContext = createContext<{
  go: (to: string) => void;
  leaving: boolean;
}>({
  go: (to) => window.location.assign(to),
  leaving: false,
});

export const useAuthNav = () => useContext(AuthNavContext);

const prefersReducedMotion = () =>
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** In-app link between auth pages; keeps modifier-clicks as normal links. */
export const AuthLink: React.FC<{
  to: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ to, children, sx }) => {
  const { go } = useAuthNav();
  return (
    <Link
      href={to}
      underline="hover"
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        go(to);
      }}
      sx={[{ fontWeight: 700 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {children}
    </Link>
  );
};

/* ------------------------------------------------------------------ */
/* Staggered entrance for form fields                                  */
/* ------------------------------------------------------------------ */

const staggerSx = (startMs: number) => {
  const delays: Record<string, object> = {};
  for (let i = 0; i < 16; i++) {
    delays[`& > *:nth-child(${i + 1})`] = {
      animationDelay: `${startMs + i * 60}ms`,
    };
  }
  return {
    "@keyframes authFieldIn": {
      from: { opacity: 0, transform: "translateY(14px)" },
      to: { opacity: 1, transform: "none" },
    },
    "& > *": {
      animation: "authFieldIn 0.55s cubic-bezier(0.2, 0, 0, 1) backwards",
    },
    ...delays,
  };
};

/**
 * Each direct child slides in after the previous one. Remount it (change its
 * `key`) to replay, e.g. per step of a multi-step form.
 */
export const AuthStagger: React.FC<
  React.ComponentProps<typeof Box> & {
    startMs?: number;
    component?: React.ElementType;
    onSubmit?: React.FormEventHandler;
  }
> = ({ startMs = 0, sx, ...rest }) => (
  <Box
    {...rest}
    sx={[staggerSx(startMs), ...(Array.isArray(sx) ? sx : [sx])]}
  />
);

/* ------------------------------------------------------------------ */
/* Left panel: brand, a live-looking week calendar, per-page copy      */
/* ------------------------------------------------------------------ */

const PANEL_COPY: Record<string, { title: string; text: string }> = {
  "/login": {
    title: "Your whole schedule, in one clear view.",
    text: "Bookings, clients, payments and reports for any business that runs on appointments.",
  },
  "/signup": {
    title: "Open your calendar in minutes.",
    text: "Create your organization, add your services and start taking bookings — online and at the front desk.",
  },
  "/forgot-password": {
    title: "Locked out? Let's get you back in.",
    text: "Confirm your email with a one-time code, then choose a new password. It only takes a minute.",
  },
};

const BLOCK_COLORS = ["#3b82f6", "#14b8a6", "#f59e0b", "#8b5cf6", "#f43f5e"];
// [day 0-6, start row 0-7, length in rows, colour index]
const BLOCKS: [number, number, number, number][] = [
  [0, 0, 2, 0],
  [0, 3, 1, 2],
  [1, 1, 2, 1],
  [1, 5, 2, 3],
  [2, 0, 1, 4],
  [2, 2, 3, 0],
  [3, 4, 2, 2],
  [3, 1, 1, 3],
  [4, 0, 2, 1],
  [4, 3, 1, 0],
  [4, 5, 2, 4],
  [5, 2, 2, 2],
  [6, 1, 1, 1],
  [6, 4, 2, 0],
];
const HOURS = ["9", "10", "11", "12", "1", "2", "3", "4"];
const ROW_H = 30;

const WeekCalendar: React.FC = () => {
  const days = useMemo(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return {
        label: d.toLocaleDateString("en-US", { weekday: "short" }),
        date: d.getDate(),
        isToday: d.toDateString() === today.toDateString(),
        month: d.toLocaleDateString("en-US", { month: "short" }),
      };
    });
  }, []);
  const range = `${days[0].month} ${days[0].date} – ${days[6].month} ${days[6].date}`;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 520,
        bgcolor: "#fff",
        color: "#0f172a",
        borderRadius: 4,
        boxShadow: "0 30px 60px rgba(8, 40, 90, 0.35)",
        p: 2.5,
        "@keyframes calSlot": {
          "0%": { opacity: 0, transform: "scale(0.92)" },
          "6%": { opacity: 1, transform: "none" },
          "88%": { opacity: 1, transform: "none" },
          "96%, 100%": { opacity: 0, transform: "scale(0.96)" },
        },
        "@keyframes calNow": {
          from: { top: "18%" },
          to: { top: "72%" },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>This week</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>{range}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            p: 0.4,
            borderRadius: 2,
            bgcolor: "#f1f5f9",
            fontSize: "0.72rem",
            fontWeight: 600,
          }}
        >
          {["Day", "Week", "Month"].map((v) => (
            <Box
              key={v}
              sx={{
                px: 1.1,
                py: 0.35,
                borderRadius: 1.5,
                color: v === "Week" ? "#0f172a" : "#64748b",
                bgcolor: v === "Week" ? "#fff" : "transparent",
                boxShadow: v === "Week" ? "0 1px 2px rgba(15,23,42,0.12)" : "none",
              }}
            >
              {v}
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "28px repeat(7, 1fr)",
          columnGap: 0.75,
        }}
      >
        <Box />
        {days.map((d) => (
          <Box key={d.label} sx={{ textAlign: "center", pb: 1 }}>
            <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", fontWeight: 600 }}>
              {d.label}
            </Typography>
            <Box
              sx={{
                mx: "auto",
                mt: 0.25,
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: d.isToday ? "#fff" : "#0f172a",
                bgcolor: d.isToday ? COLORS.MainColor : "transparent",
              }}
            >
              {d.date}
            </Box>
          </Box>
        ))}

        {/* hour labels */}
        <Box sx={{ display: "grid", gridTemplateRows: `repeat(${HOURS.length}, ${ROW_H}px)` }}>
          {HOURS.map((h) => (
            <Typography
              key={h}
              sx={{ fontSize: "0.62rem", color: "#94a3b8", lineHeight: 1, mt: "-4px" }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* the 7 day columns, drawn as one overlay so blocks can span rows */}
        <Box
          sx={{
            gridColumn: "2 / -1",
            position: "relative",
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gridTemplateRows: `repeat(${HOURS.length}, ${ROW_H}px)`,
            columnGap: 0.75,
            backgroundImage: `repeating-linear-gradient(to bottom, #eef2f7 0, #eef2f7 1px, transparent 1px, transparent ${ROW_H}px)`,
          }}
        >
          {BLOCKS.map(([day, start, len, color], i) => {
            const c = BLOCK_COLORS[color];
            return (
              <Box
                key={i}
                sx={{
                  gridColumn: day + 1,
                  gridRow: `${start + 1} / span ${len}`,
                  m: "2px 0",
                  borderRadius: 1.5,
                  bgcolor: alpha(c, 0.14),
                  borderLeft: `3px solid ${c}`,
                  px: 0.6,
                  py: 0.5,
                  overflow: "hidden",
                  animation: "calSlot 12s cubic-bezier(0.2, 0, 0, 1) infinite both",
                  animationDelay: `${i * 0.28}s`,
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
              >
                <Box sx={{ height: 4, width: "70%", borderRadius: 2, bgcolor: alpha(c, 0.55) }} />
                {len > 1 && (
                  <Box sx={{ mt: 0.5, height: 4, width: "45%", borderRadius: 2, bgcolor: alpha(c, 0.3) }} />
                )}
              </Box>
            );
          })}
          {/* "now" line drifting through the day */}
          <Box
            sx={{
              position: "absolute",
              left: -4,
              right: 0,
              height: 0,
              borderTop: "2px solid #ef4444",
              animation: "calNow 24s ease-in-out infinite alternate",
              "@media (prefers-reduced-motion: reduce)": { animation: "none", top: "40%" },
              "&::before": {
                content: '""',
                position: "absolute",
                left: -3,
                top: -5,
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "#ef4444",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const Showcase: React.FC<{ pathname: string; leaving: boolean }> = ({
  pathname,
  leaving,
}) => {
  const copy = PANEL_COPY[pathname] ?? PANEL_COPY["/login"];
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        gap: 5,
        p: { md: 5, lg: 6 },
        // Same blue as the dashboard, with one faint light for depth.
        bgcolor: COLORS.MainColor,
        color: "#fff",
        backgroundImage:
          "radial-gradient(900px 500px at -10% -20%, rgba(255,255,255,0.16), transparent 60%)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            bgcolor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box component="img" src={logo} alt="" sx={{ width: 22, height: 22 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
          Softwarezz
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.75)" }}>
          Booking System
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <WeekCalendar />
      </Box>

      <Box
        key={pathname}
        sx={{
          maxWidth: 520,
          minHeight: 132,
          "@keyframes panelCopyIn": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "none" },
          },
          animation: "panelCopyIn 0.6s cubic-bezier(0.2, 0, 0, 1) backwards",
          ...(leaving
            ? {
                opacity: 0,
                transform: "translateY(-8px)",
                transition: `opacity ${LEAVE_MS}ms ease, transform ${LEAVE_MS}ms ease`,
              }
            : {}),
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { md: "1.9rem", lg: "2.3rem" },
            lineHeight: 1.15,
            letterSpacing: "-0.025em",
          }}
        >
          {copy.title}
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: "1rem", color: "rgba(255,255,255,0.82)", maxWidth: 440 }}>
          {copy.text}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)" }}>
        © {new Date().getFullYear()} Softwarezz
      </Typography>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Shell (layout route) and page block                                 */
/* ------------------------------------------------------------------ */

const WIDE_PAGES = new Set(["/signup"]);

/**
 * Layout route for /login, /signup and /forgot-password. The left panel stays
 * mounted, so switching pages only swaps its copy and the form.
 */
const AuthShell: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const go = useCallback(
    (to: string) => {
      if (to === pathname) return;
      window.clearTimeout(timer.current);
      if (prefersReducedMotion()) {
        navigate(to);
        return;
      }
      setLeaving(true);
      timer.current = window.setTimeout(() => {
        navigate(to);
        // Safety net in case the navigation never lands.
        timer.current = window.setTimeout(() => setLeaving(false), 1000);
      }, LEAVE_MS);
    },
    [navigate, pathname]
  );
  // Router applies navigations as a transition, so clearing `leaving` right
  // after navigate() briefly re-showed the old form. Clear it only once the
  // new page has rendered, before paint; its fields then animate in.
  useLayoutEffect(() => {
    window.clearTimeout(timer.current);
    setLeaving(false);
  }, [pathname]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const nav = useMemo(() => ({ go, leaving }), [go, leaving]);

  return (
    <AuthNavContext.Provider value={nav}>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr", lg: "1.25fr 1fr" },
          bgcolor: COLORS.WhiteBackground,
        }}
      >
        <Showcase pathname={pathname} leaving={leaving} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2.5, sm: 5 },
            py: { xs: 4, sm: 6 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: WIDE_PAGES.has(pathname) ? 520 : 400,
              transition: "max-width 0.35s cubic-bezier(0.2, 0, 0, 1)",
            }}
          >
            {/* Brand for phones, where the left panel is hidden */}
            <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.25, mb: 4 }}>
              <Box component="img" src={logo} alt="" sx={{ width: 30, height: 30 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>Softwarezz</Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                Booking System
              </Typography>
            </Box>
            <Box
              sx={
                // Fade out on leave only; coming back is instant because the
                // new page's fields start hidden and stagger in themselves.
                leaving
                  ? {
                      opacity: 0,
                      transform: "translateY(-8px)",
                      transition: `opacity ${LEAVE_MS}ms ease, transform ${LEAVE_MS}ms ease`,
                    }
                  : undefined
              }
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>
    </AuthNavContext.Provider>
  );
};

interface AuthPageProps {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Under the form, e.g. the link to the other auth page. */
  footer?: React.ReactNode;
  /** Change it to replay the entrance (multi-step forms). */
  stepKey?: string | number;
  hideHeading?: boolean;
}

/** Heading + body of one auth page; the body's children animate in one by one. */
export const AuthPage: React.FC<AuthPageProps> = ({
  title,
  subtitle,
  children,
  footer,
  stepKey = "page",
  hideHeading = false,
}) => (
  <Box>
    {!hideHeading && (
      <AuthStagger key={`heading-${stepKey}`} sx={{ mb: 3.5 }}>
        <Typography
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.7rem", sm: "1.95rem" },
            letterSpacing: "-0.025em",
            color: COLORS.typographyColor,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: "text.secondary", mt: 0.75, fontSize: "0.95rem" }}>
            {subtitle}
          </Typography>
        )}
      </AuthStagger>
    )}
    <Box key={`body-${stepKey}`}>{children}</Box>
    {footer && (
      <AuthStagger startMs={450} sx={{ mt: 4, textAlign: "center" }}>
        <Typography component="div" sx={{ color: "text.secondary", fontSize: "0.92rem" }}>
          {footer}
        </Typography>
      </AuthStagger>
    )}
  </Box>
);

/** Small label above an auth field. */
export const AuthFieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontSize: "0.85rem", fontWeight: 600, color: COLORS.typographyColor, mb: 0.75 }}
  >
    {children}
  </Typography>
);

/** Shared style for the main submit button. */
export const authSubmitSx = {
  borderRadius: 2.5,
  py: 1.35,
  fontWeight: 600,
  fontSize: "0.98rem",
  textTransform: "none",
  bgcolor: COLORS.MainColor,
  boxShadow: "none",
  transition: "background-color 0.2s, box-shadow 0.2s",
  "&:hover": {
    bgcolor: "#1565c0",
    boxShadow: `0 8px 20px ${alpha(COLORS.MainColor, 0.3)}`,
  },
};

export const AUTH_FIELD_BG = "#F4F6FA";

export default AuthShell;
